import AddIcon from "@mui/icons-material/Add";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import DeleteIcon from "@mui/icons-material/Delete";
import PercentIcon from "@mui/icons-material/Percent";
import { LoadingButton } from "@mui/lab";
import { Box, Stack, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import {
	useUpdateDiscountProfileMutation,
	useCreateDiscountProfileMutation,
	DiscountProfile,
	DiscountProfileEntry,
} from "@repo/server-api";
import { useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect, useRef } from "react";
import * as Yup from "yup";

import { validErrorState } from "@repo/legacy/components/helper/Index";

import NumberTextField from "../fields/NumberTextField";

export default function DialogDiscountProfile(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		profile: DiscountProfile;
		refreshData: () => void;
	}>,
) {
	const { t } = useTranslation();

	const { showNotification } = useNotification();
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});

	const [open, setOpen] = useState(false);

	const [profile, setProfile] = useState<DiscountProfile>({
		id: "",
		entries: [],
		maxDueDate: 0,
		name: "",
		version: 0,
		isDeleted: false,
	});

	const maxDueDateRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(t("validation.name.required")),
		entries: Yup.array().min(1, t("validation.entries.required")),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(profile, { abortEarly: false });
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		localErrors.maxDueDate = (await maxDueDateRef.current?.validate()) ?? "";

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const [updateDiscountProfile] = useUpdateDiscountProfileMutation({
		variables: {
			id: profile?.id,
			name: profile?.name,
			maxDueDate: profile?.maxDueDate,
			entries: profile?.entries,
		},
		onCompleted(data) {
			if (data.updateDiscountProfile.discountProfile) {
				props.refreshData();
				handleClose();
				showNotification(t("profiles.updatedProfile"), "success");
			}
			if (data.updateDiscountProfile.errors) {
				data.updateDiscountProfile.errors.forEach((err) => {
					if ("errors" in err && Array.isArray(err.errors)) {
						err.errors.forEach((e) => {
							showNotification(e.errorMessage, "error");
						});
					} else {
						showNotification(err.message, "error");
					}
				});
			}
			setSaveClicked(false);
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [createDiscountProfile] = useCreateDiscountProfileMutation({
		variables: {
			name: profile?.name,
			maxDueDate: profile?.maxDueDate,
			entries: profile?.entries,
		},
		onCompleted(data) {
			if (data.createDiscountProfile.discountProfile) {
				props.refreshData();
				handleClose();
				showNotification(t("profiles.createdProfile"), "success");
			}
			if (data.createDiscountProfile.errors) {
				data.createDiscountProfile.errors.forEach((err) => {
					if ("errors" in err && Array.isArray(err.errors))
						err.errors.forEach((e) => {
							showNotification(e.errorMessage, "error");
						});
					else {
						showNotification(err.message, "error");
					}
				});
			}
			setSaveClicked(false);
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	useEffect(() => {
		setOpen(props.open);

		if (props.profile) {
			const entries = props.profile.entries.map((e) => {
				return {
					id: e.id,
					discount: typeof e.discount === "number" ? e.discount : 0,
					dueDate: typeof e.dueDate === "number" ? e.dueDate : 0,
				};
			});

			setProfile({ ...props.profile, entries: entries });
		}
	}, [props.open]);

	const handleClose = () => {
		setOpen(false);
		props.setOpen(false);
		setSaveClicked(false);
		setErrors({});
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.stopPropagation();
		event.preventDefault();

		const isValid = await validateFields();

		if (isValid) {
			setSaveClicked(true);
			const entries = profile?.entries?.map((e) => {
				return {
					discount: e.discount,
					dueDate: e.dueDate,
				};
			});
			if (profile?.id) {
				updateDiscountProfile({
					variables: {
						id: profile?.id,
						name: profile?.name,
						maxDueDate: profile?.maxDueDate,
						entries: entries,
					},
				});
			} else {
				createDiscountProfile({
					variables: {
						name: profile?.name,
						maxDueDate: profile?.maxDueDate,
						entries: entries,
					},
				});
			}
		}
	};

	const columns: GridColDef<DiscountProfileEntry>[] = [
		{
			field: "discount",
			headerName: t("discount"),
			width: 300,
			sortable: true,
			renderCell(params: GridCellParams) {
				return (
					<NumberTextField
						isLoaded={true}
						value={params.row.discount}
						setValue={(value) => {
							setProfile({
								...profile,
								entries: profile?.entries?.map((e) =>
									e.id === params.row.id ? { ...e, discount: value ?? 0 } : e,
								),
							});
						}}
						flat={true}
						minValue={0}
						maxValue={100}
						icon={<PercentIcon />}
					/>
				);
			},
		},
		{
			field: "dueDate",
			headerName: t("due-date"),
			width: 300,
			sortable: true,
			renderCell(params: GridCellParams) {
				return (
					<NumberTextField
						isLoaded={true}
						value={params.row.dueDate}
						setValue={(value) => {
							setProfile({
								...profile,
								entries: profile?.entries?.map((e) =>
									e.id === params.row.id ? { ...e, dueDate: value ?? 0 } : e,
								),
							});
						}}
						flat={true}
						integer={true}
						icon={<CalendarTodayIcon />}
					/>
				);
			},
		},
		{
			field: "actions",
			headerName: t("actions"),
			sortable: false,
			width: 250,
			renderCell: (params: GridCellParams) => {
				const deleteEntry = (id: string) => {
					const entries = [...(profile?.entries ?? [])];
					entries?.splice(
						entries.findIndex((e) => e.id === id),
						1,
					);
					setProfile({ ...profile, entries });
				};

				return (
					<div>
						<Button onClick={() => deleteEntry(params.row.id)}>
							<DeleteIcon />
						</Button>
					</div>
				);
			},
		},
	];

	const addEntry = () => {
		setErrors({ ...errors, entries: "" });
		const entry: DiscountProfileEntry = {
			id: profile.entries.length.toString(),
			discount: 0,
			dueDate: 0,
		};
		setProfile({ ...profile, entries: [...profile.entries, entry] });
	};

	return (
		<Dialog
			open={open}
			PaperProps={{
				component: "form",
				onSubmit: handleSubmit,
			}}
			maxWidth="md"
			fullWidth={true}
			data-testid="discount-profile-dialog">
			<DialogTitle>
				{profile.id ? t("edit-discount-profile") : t("create-discount-profile")}
			</DialogTitle>
			<DialogContent>
				<Stack spacing={3} sx={{ pt: 1 }}>
					<Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
						<TextField
							onChange={(event) => {
								setProfile({ ...profile, name: event.target.value ?? "" });
								setErrors({ ...errors, name: "" });
							}}
							error={!!errors.name}
							helperText={errors.name as string}
							value={profile.name}
							label={t("name")}
							variant="outlined"
							sx={{ flex: 2 }}
							data-testid="discount-profile-name-input"
						/>
						<Box sx={{ flex: 1 }}>
							<NumberTextField
								isLoaded={true}
								ref={maxDueDateRef}
								value={profile.maxDueDate}
								setValue={(value) => {
									setProfile({ ...profile, maxDueDate: value ?? 0 });
								}}
								label={t("max-due-date")}
								errors={errors.maxDueDate as Record<string, boolean | object | string>}
								setErrors={(value) => setErrors({ ...errors, maxDueDate: value })}
								icon={<CalendarTodayIcon />}
								integer={true}
							/>
						</Box>
					</Box>
					<Box sx={{ height: "50vh", width: "100%" }} data-testid="discount-profile-entries-grid">
						<DataGridPremium
							getRowId={(row: DiscountProfileEntry) => (row.id ? row.id : "")}
							rows={profile.entries}
							columns={columns}
							disableRowSelectionOnClick
							className="table"
							hideFooterPagination={true}
							localeText={{
								footerTotalRows: t("pagination.total-rows"),
							}}
							initialState={{
								sorting: {
									sortModel: [{ field: "dueDate", sort: "asc" }],
								},
							}}
						/>
					</Box>
					{errors.entries && (
						<Box sx={{ color: "error.main" }} data-testid="discount-profile-entries-error">
							{errors.entries as string}
						</Box>
					)}
					<Button
						variant="contained"
						onClick={addEntry}
						startIcon={<AddIcon />}
						data-testid="discount-profile-add-entry-button">
						{t("add-entry")}
					</Button>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button variant="outlined" onClick={handleClose} data-testid="discount-profile-cancel-button">
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					type="submit"
					variant="contained"
					className={saveClicked ? "" : "blueButton"}
					data-testid="discount-profile-save-button">
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
}
